// SCR-STAFF-886 — سال و دوره‌های مالی (US-ACC-030، 031): سال مالی شمسی با ۱۲ دوره ماهانه، قفل دوره (acc.period.lock) با تأیید و پیام اثر
// (رویداد دیرهنگام روی نخستین روز باز بعدی — AC-ACC-011.2)، بازگشایی فقط برای مالک مرکز با دلیل (BR-ACC-031؛ AP-ACC-02)، ایجاد سال بعد.
// AP-ACC-03 (US-ACC-032، 033): مانده افتتاحیه سال با مقایسه با مانده کنترلی ماژول‌ها و بستن سال با پیش‌نمایش آمادگی (BR-ACC-032).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { unwrap, type AccFiscalPeriodView, type AccOpeningBalanceResponse } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  ConfirmDialog,
  DataTable,
  EmptyState,
  ErrorState,
  faDigits,
  FormDialog,
  JalaliDate,
  KeyValueList,
  ListPage,
  LoadingState,
  ReasonDialog,
  type DataTableColumn,
} from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { accKeys, Amount, num, PeriodStatusChip, SCR_STAFF_886, useAccBranches } from './common';

export function FiscalPage() {
  const { t } = useTranslation('acc');
  const { tenantApi, session, activeTenant } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const full = access.accessMode === 'Full';
  const canLock = full && access.can('acc.period.lock');
  const canManage = full && access.can('acc.period.manage');
  const years = useQuery({
    queryKey: accKeys.fiscal(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/fiscal-years')),
  });
  const [locking, setLocking] = useState<AccFiscalPeriodView | null>(null);
  const [reopening, setReopening] = useState<AccFiscalPeriodView | null>(null);
  // BR-ACC-031: بازگشایی فقط مالک مرکز (سرور هم PHX-ACC-0404 برمی‌گرداند).
  const canReopen = canLock && activeTenant?.isOwner === true;
  // AP-ACC-03 (US-ACC-032، 033): مانده افتتاحیه و بستن سال پشت Feature دفتر جامع و مجوز HighlySensitive بستن سال.
  const fullLedger = access.featureEnabled('feature.acc.full_ledger');
  const canCloseYear = full && access.can('acc.year.close');
  const refresh = () => void queryClient.invalidateQueries({ queryKey: accKeys.fiscal(session?.tenantId) });
  const lock = useMutation({
    mutationFn: (id: string) =>
      unwrap(tenantApi.POST('/api/v1/accounting/fiscal-periods/{id}:lock', { params: { path: { id } } })),
    onSuccess: () => {
      setLocking(null);
      refresh();
    },
  });
  const reopen = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/fiscal-periods/{id}:reopen', {
          params: { path: { id } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      setReopening(null);
      refresh();
    },
  });
  const items = years.data?.items ?? [];
  const nextYear = items.length > 0 ? Math.max(...items.map((year) => num(year.year))) + 1 : null;
  const create = useMutation({
    mutationFn: (year: number) =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/fiscal-years', {
          body: { year },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: refresh,
  });
  const columns: DataTableColumn<AccFiscalPeriodView>[] = [
    { id: 'month', headerKey: 'acc:fiscal.month', cell: (row) => faDigits(num(row.month)) },
    { id: 'start', headerKey: 'acc:fiscal.start', cell: (row) => <JalaliDate value={row.startDate} /> },
    { id: 'end', headerKey: 'acc:fiscal.end', cell: (row) => <JalaliDate value={row.endDate} /> },
    { id: 'status', headerKey: 'acc:common.status', cell: (row) => <PeriodStatusChip status={row.status} /> },
    {
      id: 'locked',
      headerKey: 'acc:fiscal.locked_at',
      cell: (row) => (row.lockedAt ? <JalaliDate value={row.lockedAt} withTime /> : '—'),
    },
    {
      id: 'reopened',
      headerKey: 'acc:fiscal.reopened',
      cell: (row) =>
        row.reopenedAt ? (
          <span data-testid="acc-period-reopened">
            <JalaliDate value={row.reopenedAt} withTime /> — {row.reopenReason}
          </span>
        ) : (
          '—'
        ),
    },
    {
      id: 'actions',
      headerKey: 'acc:common.actions',
      cell: (row) =>
        canLock && row.status === 'Open' ? (
          <Button
            size="small"
            onClick={() => setLocking(row)}
            data-testid="acc-period-lock"
            data-month={num(row.month)}
          >
            {t('acc:fiscal.lock')}
          </Button>
        ) : canReopen && row.status === 'Locked' ? (
          <Button
            size="small"
            color="warning"
            onClick={() => setReopening(row)}
            data-testid="acc-period-reopen"
            data-month={num(row.month)}
          >
            {t('acc:fiscal.reopen')}
          </Button>
        ) : null,
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_886}
      titleKey="acc:fiscal.title"
      subtitleKey="acc:fiscal.subtitle"
      actions={
        canManage && nextYear !== null ? (
          <Button
            variant="outlined"
            onClick={() => create.mutate(nextYear)}
            disabled={create.isPending}
            data-testid="acc-year-create"
          >
            {t('acc:fiscal.add_next_year', { year: faDigits(nextYear) })}
          </Button>
        ) : undefined
      }
    >
      {create.error ? <ErrorState error={create.error} compact /> : null}
      {years.isPending ? <LoadingState rows={4} /> : null}
      {years.error ? <ErrorState error={years.error} onRetry={() => void years.refetch()} /> : null}
      {years.data && items.length === 0 ? <EmptyState titleKey="acc:fiscal.empty" /> : null}
      <Stack spacing={2}>
        {items.map((year) => (
          <Paper
            key={year.id}
            variant="outlined"
            sx={{ p: 2 }}
            data-testid="acc-fiscal-year"
            data-year={num(year.year)}
          >
            <Typography variant="subtitle1" gutterBottom>
              {t('acc:fiscal.year')} {faDigits(num(year.year))}
            </Typography>
            {/* AP-ACC-03: مانده افتتاحیه و بستن سال فقط با Feature دفتر جامع و مجوز acc.year.close. */}
            {fullLedger && canCloseYear ? <YearClosePanel year={num(year.year)} onChanged={refresh} /> : null}
            <DataTable
              captionKey="acc:fiscal.title"
              columns={columns}
              rows={year.periods}
              getRowId={(row) => row.id}
              emptyTitleKey="acc:fiscal.empty"
            />
          </Paper>
        ))}
      </Stack>
      <ConfirmDialog
        open={locking !== null}
        titleKey="acc:fiscal.lock"
        messageKey="acc:fiscal.lock_message"
        confirmKey="acc:fiscal.lock"
        destructive
        busy={lock.isPending}
        error={lock.error}
        onConfirm={() => locking && lock.mutate(locking.id)}
        onCancel={() => setLocking(null)}
      />
      <ReasonDialog
        open={reopening !== null}
        titleKey="acc:fiscal.reopen"
        messageKey="acc:fiscal.reopen_message"
        confirmKey="acc:fiscal.reopen"
        destructive
        busy={reopen.isPending}
        error={reopen.error}
        onConfirm={(reason) => reopening && reopen.mutate({ id: reopening.id, reason })}
        onCancel={() => setReopening(null)}
      />
    </ListPage>
  );
}

/**
 * مانده افتتاحیه و بستن سال یک سال مالی (US-ACC-032، 033؛ AP-ACC-03).
 * آمادگی بستن از سرور می‌آید تا دلیل نبودِ اجازه (دوره باز، صف خطا، نگاشت نشدن سود و زیان انباشته) پیش از اقدام دیده شود (BR-ACC-032).
 */
function YearClosePanel({ year, onChanged }: { year: number; onChanged: () => void }) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const [opening, setOpening] = useState(false);
  const [closing, setClosing] = useState(false);
  const readiness = useQuery({
    queryKey: accKeys.readiness(session?.tenantId, year),
    queryFn: () =>
      unwrap(
        tenantApi.GET('/api/v1/accounting/fiscal-years/{fiscalYear}/close-readiness', {
          params: { path: { fiscalYear: year } },
        }),
      ),
  });
  const close = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/fiscal-years/{fiscalYear}:close', {
          params: { path: { fiscalYear: year } },
        }),
      ),
    onSuccess: () => {
      setClosing(false);
      void readiness.refetch();
      onChanged();
    },
  });
  const data = readiness.data;
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="acc-year-close" data-year={year}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <Typography variant="subtitle2">{t('acc:close.title')}</Typography>
        {readiness.isPending ? <LoadingState rows={1} /> : null}
        {readiness.error ? <ErrorState error={readiness.error} compact /> : null}
        {data ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center">
            <Chip
              size="small"
              label={t('acc:close.open_periods', { count: num(data.openPeriods) })}
              color={num(data.openPeriods) === 0 ? 'success' : 'warning'}
              data-testid="acc-close-open-periods"
            />
            <Chip
              size="small"
              label={t('acc:close.failed_entries', { count: num(data.failedEntries) })}
              color={num(data.failedEntries) === 0 ? 'success' : 'warning'}
              data-testid="acc-close-failed"
            />
            <Chip
              size="small"
              label={
                data.hasRetainedEarningsAccount ? t('acc:close.retained_ok') : t('acc:close.retained_missing')
              }
              color={data.hasRetainedEarningsAccount ? 'success' : 'warning'}
              data-testid="acc-close-retained"
            />
            <span data-testid="acc-close-net" data-amount={num(data.netResult.amount)}>
              {t('acc:close.net_result')}: <Amount money={data.netResult} />
            </span>
          </Stack>
        ) : null}
        <Stack direction="row" spacing={1} sx={{ marginInlineStart: 'auto' }}>
          <Button size="small" onClick={() => setOpening(true)} data-testid="acc-opening-open">
            {t('acc:opening.title')}
          </Button>
          <Button
            size="small"
            color="warning"
            variant="contained"
            disabled={!(data?.canClose ?? false)}
            onClick={() => setClosing(true)}
            data-testid="acc-close-run"
          >
            {t('acc:close.close')}
          </Button>
        </Stack>
      </Stack>
      {data?.isClosed ? (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="acc-close-closed">
          {t('acc:close.already_closed')}
        </Alert>
      ) : null}
      {close.data ? (
        <Alert severity="success" sx={{ mt: 2 }} data-testid="acc-close-result">
          {t('acc:close.result', {
            closing: close.data.closingNumber,
            opening: close.data.openingNumber ?? t('acc:common.none'),
          })}
        </Alert>
      ) : null}
      <ConfirmDialog
        open={closing}
        titleKey="acc:close.close"
        messageKey="acc:close.close_message"
        confirmKey="acc:close.close"
        destructive
        busy={close.isPending}
        error={close.error}
        onConfirm={() => close.mutate()}
        onCancel={() => setClosing(false)}
      />
      {opening ? (
        <OpeningBalanceDialog
          year={year}
          onDone={() => {
            void readiness.refetch();
            onChanged();
          }}
          onClose={() => setOpening(false)}
        />
      ) : null}
    </Paper>
  );
}

interface OpeningLineForm {
  accountId: string;
  detail1Id: string;
  debit: string;
  credit: string;
}

const EMPTY_OPENING_LINE: OpeningLineForm = { accountId: '', detail1Id: '', debit: '', credit: '' };

function openingRials(value: string): number {
  const digits = value.replace(/[^\d۰-۹٠-٩]/g, '').replace(/[۰-۹٠-٩]/g, (char) => {
    const code = char.charCodeAt(0);
    return String(code >= 0x0660 && code <= 0x0669 ? code - 0x0660 : code - 0x06f0);
  });
  return digits ? Number(digits) : 0;
}

/**
 * ثبت مانده افتتاحیه سال (AC-ACC-032.1): یک سند قطعی در نخستین روز سال، فقط یک بار.
 * پاسخ، مغایرت مانده حساب‌های کنترلی با مانده ماژول‌ها را برمی‌گرداند تا پیش از شروع گردش سال اصلاح شود (C-AGENT-1772).
 */
function OpeningBalanceDialog({
  year,
  onDone,
  onClose,
}: {
  year: number;
  onDone: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('acc');
  const { tenantApi, session } = useTenantSession();
  const key = useIdempotencyKey();
  const branches = useAccBranches();
  const [branchId, setBranchId] = useState(branches[0]?.branchId ?? '');
  const [lines, setLines] = useState<OpeningLineForm[]>([
    { ...EMPTY_OPENING_LINE },
    { ...EMPTY_OPENING_LINE },
  ]);
  const [result, setResult] = useState<AccOpeningBalanceResponse | null>(null);
  const accounts = useQuery({
    queryKey: accKeys.accounts(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/chart/accounts')),
  });
  const details = useQuery({
    queryKey: accKeys.details(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/accounting/detail-accounts')),
  });
  const leafAccounts = (accounts.data?.items ?? []).filter(
    (account) =>
      account.isActive && !(accounts.data?.items ?? []).some((child) => child.parentId === account.id),
  );
  const detailItems = (details.data?.items ?? []).filter((detail) => detail.isActive);
  const debit = lines.reduce((sum, line) => sum + openingRials(line.debit), 0);
  const credit = lines.reduce((sum, line) => sum + openingRials(line.credit), 0);
  const post = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/accounting/fiscal-years/opening-balance', {
          body: {
            year,
            branchId,
            lines: lines
              .filter((line) => line.accountId !== '')
              .map((line) => ({
                accountId: line.accountId,
                detail1Id: line.detail1Id || null,
                debit:
                  openingRials(line.debit) > 0 ? { amount: openingRials(line.debit), currency: 'IRR' } : null,
                credit:
                  openingRials(line.credit) > 0
                    ? { amount: openingRials(line.credit), currency: 'IRR' }
                    : null,
              })),
          },
          headers: { 'Idempotency-Key': key.current() },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (data) => {
      setResult(data);
      onDone();
    },
  });
  const setLine = (index: number, patch: Partial<OpeningLineForm>) =>
    setLines((current) =>
      current.map((line, position) => (position === index ? { ...line, ...patch } : line)),
    );
  if (result) {
    return (
      <FormDialog
        open
        titleKey="acc:opening.title"
        maxWidth="md"
        isDirty={false}
        isSubmitting={false}
        submitKey="acc:common.cancel"
        onSubmit={onClose}
        onClose={onClose}
      >
        <Alert severity="success" data-testid="acc-opening-posted">
          {t('acc:opening.posted', { number: result.number })}
        </Alert>
        <Typography variant="subtitle2">{t('acc:opening.variances_title')}</Typography>
        {result.variances.length === 0 ? (
          <Typography variant="body2" color="text.secondary" data-testid="acc-opening-variances-empty">
            {t('acc:opening.variances_empty')}
          </Typography>
        ) : (
          <Table size="small" aria-label={t('acc:opening.variances_title')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('acc:mapping.role')}</TableCell>
                <TableCell>{t('acc:common.code')}</TableCell>
                <TableCell align="right">{t('acc:opening.opening_amount')}</TableCell>
                <TableCell align="right">{t('acc:opening.module_amount')}</TableCell>
                <TableCell align="right">{t('acc:opening.difference')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.variances.map((row) => (
                <TableRow key={row.role} data-testid="acc-opening-variance" data-role={row.role}>
                  <TableCell>{row.role}</TableCell>
                  <TableCell>
                    <bdi dir="ltr">{row.accountCode ?? '—'}</bdi>
                  </TableCell>
                  <TableCell align="right">
                    <Amount money={row.openingAmount} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount money={row.moduleAmount} />
                  </TableCell>
                  <TableCell align="right">
                    <Amount money={row.difference} testId="acc-opening-difference" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </FormDialog>
    );
  }
  return (
    <FormDialog
      open
      titleKey="acc:opening.title"
      maxWidth="lg"
      isDirty={lines.some((line) => line.accountId !== '')}
      isSubmitting={post.isPending}
      submitError={post.error}
      submitKey="acc:opening.post"
      onSubmit={() => post.mutate()}
      onClose={onClose}
    >
      <KeyValueList
        columns={2}
        items={[
          { labelKey: 'acc:fiscal.year', value: faDigits(year) },
          { labelKey: 'acc:opening.hint', value: t('acc:opening.hint_value') },
        ]}
      />
      <TextField
        select
        label={t('acc:common.branch')}
        value={branchId}
        required
        onChange={(event) => setBranchId(event.target.value)}
        sx={{ minWidth: 200 }}
        slotProps={{ htmlInput: { 'data-testid': 'acc-opening-branch' } }}
      >
        {branches.map((branch) => (
          <MenuItem key={branch.branchId} value={branch.branchId}>
            {branch.name}
          </MenuItem>
        ))}
      </TextField>
      {accounts.isPending || details.isPending ? <LoadingState rows={2} /> : null}
      <Paper variant="outlined" sx={{ p: 1 }}>
        <Table size="small" aria-label={t('acc:opening.title')}>
          <TableHead>
            <TableRow>
              <TableCell>{t('acc:journal.account')}</TableCell>
              <TableCell>{t('acc:journal.details')}</TableCell>
              <TableCell align="right">{t('acc:journal.debit')}</TableCell>
              <TableCell align="right">{t('acc:journal.credit')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, index) => (
              <TableRow key={index} data-testid="acc-opening-line">
                <TableCell sx={{ minWidth: 220 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={line.accountId}
                    onChange={(event) => setLine(index, { accountId: event.target.value })}
                    slotProps={{ htmlInput: { 'data-testid': `acc-opening-account-${index}` } }}
                  >
                    <MenuItem value="">{t('acc:common.none')}</MenuItem>
                    {leafAccounts.map((account) => (
                      <MenuItem key={account.id} value={account.id}>
                        <bdi dir="ltr">{account.code}</bdi>&nbsp;{account.title}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell sx={{ minWidth: 200 }}>
                  <TextField
                    select
                    size="small"
                    fullWidth
                    value={line.detail1Id}
                    onChange={(event) => setLine(index, { detail1Id: event.target.value })}
                  >
                    <MenuItem value="">{t('acc:common.none')}</MenuItem>
                    {detailItems.map((detail) => (
                      <MenuItem key={detail.id} value={detail.id}>
                        <bdi dir="ltr">{detail.code}</bdi>&nbsp;{detail.title}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    value={line.debit}
                    onChange={(event) => setLine(index, { debit: event.target.value, credit: '' })}
                    slotProps={{
                      htmlInput: {
                        dir: 'ltr',
                        inputMode: 'numeric',
                        'data-testid': `acc-opening-debit-${index}`,
                      },
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    value={line.credit}
                    onChange={(event) => setLine(index, { credit: event.target.value, debit: '' })}
                    slotProps={{
                      htmlInput: {
                        dir: 'ltr',
                        inputMode: 'numeric',
                        'data-testid': `acc-opening-credit-${index}`,
                      },
                    }}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    aria-label={t('acc:manual.remove_line')}
                    disabled={lines.length <= 2}
                    onClick={() => setLines((current) => current.filter((_, position) => position !== index))}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
          <Button
            size="small"
            onClick={() => setLines((current) => [...current, { ...EMPTY_OPENING_LINE }])}
            data-testid="acc-opening-add-line"
          >
            {t('acc:manual.add_line')}
          </Button>
          <Typography variant="body2" data-testid="acc-opening-total-debit" data-amount={debit}>
            {t('acc:manual.total_debit')}: {faDigits(debit)}
          </Typography>
          <Typography variant="body2" data-testid="acc-opening-total-credit" data-amount={credit}>
            {t('acc:manual.total_credit')}: {faDigits(credit)}
          </Typography>
        </Stack>
      </Paper>
      {debit !== credit ? (
        <Alert severity="warning" data-testid="acc-opening-unbalanced">
          {t('acc:manual.unbalanced')}
        </Alert>
      ) : null}
    </FormDialog>
  );
}
